/**
 * Lets the conversation page hand the widget header its "start a new chat"
 * action. The header (EmbedApp) and the conversation (EmbedBrainPage) are
 * siblings; only the page knows how to reset its thread — clear the stored
 * guest session and the signed-in thread — so a remount alone would bring the
 * same chat back from sessionStorage.
 */

import React, { createContext, useContext, useEffect } from 'react';

type NewChat = (() => void) | null;

const EmbedChatActionsContext = createContext<(newChat: NewChat) => void>(() => undefined);

export const EmbedChatActionsProvider: React.FC<{
  onNewChatChange: (newChat: NewChat) => void;
  children: React.ReactNode;
}> = ({ onNewChatChange, children }) => (
  <EmbedChatActionsContext.Provider value={onNewChatChange}>{children}</EmbedChatActionsContext.Provider>
);

/** Offer `newChat` in the header while it is set; withdraw it on unmount. */
export function useOfferNewChat(newChat: NewChat): void {
  const offer = useContext(EmbedChatActionsContext);
  useEffect(() => {
    offer(newChat);
    return () => offer(null);
  }, [offer, newChat]);
}
